import React from "react";
import GatsbyLink from "gatsby-link";
import { ContentSection, HeaderSection } from "../docs/sections";
import { Card, Content, Grid, Paragraph, TextContainer, Ul } from "../../lib/src";
import { Header } from "../docs";

/*
 * Files in this folder with default exports will show up as pages in the styleguide!
 */

export default (): JSX.Element => {
	return (
		<>
			<HeaderSection
				title="What is Fancy?"
				subTitle="Fancy is Siteimprove's design system – a unified collection of design foundations, reusable components, and clear standards that empowers product teams to build cohesive digital experiences. Fancy accelerates development while ensuring quality and consistency across all Siteimprove products. The result: accessible, intuitive experiences that reflect our brand's unique identity."
			/>
			<ContentSection>
				<TextContainer article>
					<Header.H2>Built for teams</Header.H2>
					<Paragraph>
						Fancy provides guidelines for cross-functional development teams, enabling them to
						collaborate more effectively and ensure every touchpoint delivers a consistent
						Siteimprove experience.
					</Paragraph>
					<br></br>
					<Grid flush equalHeight>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Design</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Design principles, foundational guidelines, components, patterns and Figma
											library
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/Designing/general-FAQ">Get started</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Development</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Environment set-up, contribution guidelines, component repository and
											implementation guidelines.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/Developing/Getting started in a new project">
											Get started
										</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Content</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Writing guidelines, grammar and mechanics, tone of voice and preferred words
											list.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/get-started–writing/">Get started</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
					</Grid>
					<Header.H2>Design and build with confidence</Header.H2>
					<Paragraph>
						Fancy bridges the gap between design and development with clear, actionable guidelines.
						Teams can collaborate with confidence, knowing their work will deliver a cohesive
						Siteimprove experience without endless rounds of revision.
					</Paragraph>
					<br></br>
					<Grid flush equalHeight>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Foundations</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Core elements such as typography, color and grid form the backbone of every
											Siteimprove experience.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/Designing/foundations">Explore foundations</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Patterns</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Reusable combinations of layouts and components that address common user
											objectives.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/Patterns/patterns-overview">Explore patterns</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Components</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Individual building blocks of the design system. Their systematic reuse
											creates consistency.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/Components/components-overview">Explore components</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
					</Grid>
					<Header.H2>Questions? We're here to help</Header.H2>
					<Paragraph>
						The Fancy team is active across several Slack channels, ready to support you wherever
						you are in your workflow. Whether you need design guidance, technical support, or
						accessibility expertise, we've got dedicated spaces for focused discussions.
					</Paragraph>
					<Ul
						items={[
							<>
								For design related questions, please use the{" "}
								<a
									href="https://siteimprove.slack.com/archives/CAVM2RC4F"
									target="_blank"
									rel="noreferrer"
								>
									ux_fancy
								</a>{" "}
								channel.
							</>,
							<>
								If your question is more technical, please use the{" "}
								<a
									href="https://siteimprove.slack.com/archives/CCH0J3U4E"
									target="_blank"
									rel="noreferrer"
								>
									dev_fancy
								</a>{" "}
								channel.
							</>,
						]}
					/>
				</TextContainer>
			</ContentSection>
		</>
	);
};
